/**
 * Models for raw JSON data.
 */

import { defined, Optional } from '../utils/Util';

export type JsonData = {
  [key: string]: JsonTableData;
};

export interface JsonTableData {
  columns: JsonColumn[];
  rows: { id: string; data: JsonCell[] }[];
}

export interface JsonColumn {
  id: string;
  data: string[];
  properties?: Optional<Record<string, string>>;
}

export interface JsonRow {
  id: string;
  data: JsonCell[];
}

type JsonCell = JsonValue[];

type JsonValue = Optional<string>[];

/**
 * Models for formatted JSON data.
 */

export interface TableData {
  columns: ColumnData[];
  rows: RowData[];
}

export interface ColumnData {
  title: string;
  hasMissingData: boolean;
}

export interface RowData {
  id: string;
  cells: CellData[];
}

export interface CellData {
  values: CellValue[];
}

export interface AdditionalValues {
  raceYear?: string;
  raceId?: string;
  circuit?: string;
  position?: string;
  valueTotal?: { top: number; bottom: number };
  hasMissingData?: boolean;
}
export interface CellValue {
  value: string;
  link?: string;
  diff?: string;
  additionalValues: AdditionalValues;
}

export function toData(jsonData: JsonTableData): TableData {
  const columns: ColumnData[] = [];
  const columnsInfo: string[][] = [];
  for (const jsonColumn of jsonData.columns) {
    let hasMissingData = false;
    if (defined(jsonColumn.properties)) {
      hasMissingData =
        jsonColumn.properties['is_uncertain']?.toLowerCase() === 'true';
    }
    const column: ColumnData = {
      title: jsonColumn.id,
      hasMissingData,
    };
    columns.push(column);
    columnsInfo.push(jsonColumn.data);
  }
  const rows: RowData[] = jsonData.rows.map((row) => ({
    id: row.id,
    cells: row.data.map(
      (cell, columnIndex): CellData => ({
        values: cell
          .map((data: JsonValue): Optional<CellValue> => {
            const columnInfo = columnsInfo[columnIndex];

            const value = data[columnInfo?.indexOf('value') ?? -1];
            if (!defined(value)) {
              return undefined; // never happen
            }
            const c: CellValue = {
              value,
              additionalValues: {},
            };
            for (const [index, columnKind] of columnInfo?.entries() ?? []) {
              const v = data[index];
              if (!defined(v)) {
                continue;
              }
              if (columnKind === 'link') {
                c.link = v;
              } else if (columnKind === 'diff') {
                c.diff = v;
              } else if (columnKind === 'race_year') {
                c.additionalValues.raceYear = v;
              } else if (columnKind === 'race_id') {
                c.additionalValues.raceId = v;
              } else if (columnKind === 'circuit') {
                c.additionalValues.circuit = v;
              } else if (columnKind === 'position') {
                c.additionalValues.position = v;
              } else if (columnKind === 'value_per_total') {
                if (/^[0-9]+\/[0-9]+$/.exec(v)) {
                  const [top, bottom] = v.split('/').map((v) => parseInt(v));
                  if (defined(top) && defined(bottom)) {
                    c.additionalValues.valueTotal = { top, bottom };
                  }
                }
              } else if (columnKind === 'is_uncertain') {
                c.additionalValues.hasMissingData = v.toLowerCase() === 'true';
              }
            }
            return c;
          })
          .filter((v) => v !== undefined),
      })
    ),
  }));
  return { columns, rows };
}
